<template>
  <div class="tv-resources">
    <transition name="fade" mode="out-in">
      <div class="tv-overview" v-if="!isChannelListShown" :key="'overview'">
        <base-heading>{{ $t('tvPage.pageTitle') }}</base-heading>
        <div class="channel-info">
          <div class="info-wrapper">
            <div class="info-side">
              {{ $t('tvPage.allChannelsLabel') }} <span>{{ channelsCount.allChannels || '-' }}</span>
            </div>
            <div class="info-side">
              {{ $t('tvPage.hdChannelsLabel') }} <span>{{ channelsCount.hdChannels || '-' }}</span>
            </div>
          </div>
          <base-button block size="small" @click="getChannels">{{ $t('tvPage.channelListButton') }}</base-button>
        </div>
        <template v-if="$props.resourcesData.devices">
          <div class="tv-actions" v-for="(device, index) in $props.resourcesData.devices" :key="'device'+index">
            <options-management>
              <img slot="icon" src="/assets/images/tv-devices.png" alt=""/>
              <base-button v-if="hasTVInstructions" variant="text" slot="action" class="manual-button" @click="downloadInstructions" aria-label="Download instructions">
                {{ $t('optionsManagement.downloadLabel') }} <i class="icon-sc-download-hover"></i>
              </base-button>
              <options-item>
                <template #description>
                  <p class="option-description">
                    {{ $t('optionsManagement.optionPackageLabel') }}
                  </p>
                  <h4 class="option-title">
                    {{ device.packageName }}
                  </h4>
                </template>
              </options-item>
              <options-item>
                <template #description>
                  <p class="option-description">
                    {{ $t('optionsManagement.optionNameLabel') }}
                  </p>
                  <transition name="fade" mode="out-in">
                    <h4 class="option-title" v-if="!deviceHelper[device.id] || !deviceHelper[device.id].editMode">
                      {{ device.name || '-' }}
                    </h4>
                    <validation-observer v-autofocus tag="form" v-else class="edit-name" :ref="'nameObs'+index" @submit.prevent>
                      <base-v-input variant="transparent"
                                    mode="eager"
                                    v-model="deviceHelper[device.id].name"
                                    rules="required"
                                    maxlength="50"
                      />
                    </validation-observer>
                  </transition>
                </template>
                <template #actions>
                  <transition name="fade" mode="out-in">
                    <base-button variant="text" v-if="!deviceHelper[device.id] || !deviceHelper[device.id].editMode"
                                 @click="enterEditMode(device.id, device.name)"><i class="icon-sc-edit"></i></base-button>
                    <div v-else class="nowrap">
                      <base-button variant="text" @click="editText(device.id, deviceHelper[device.id].name, index)"><i class="icon-sc-check"></i></base-button>
                      <base-button variant="text" @click="exitEditMode"><i class="icon-sc-x"></i></base-button>
                    </div>
                  </transition>
                </template>
              </options-item>
              <options-item>
                <template #description>
                  <p class="option-description">
                    {{ $t('optionsManagement.deviceIdLabel') }}
                  </p>
                  <h4 class="option-title">
                    {{ device.id }}
                  </h4>
                </template>
              </options-item>
              <div slot="footer" class="device-actions">
                <base-button variant="text" class="action-button" @click="showPinModal(device.id)">
                  {{ $t('optionsManagement.changePin') }}
                </base-button>
                <base-button variant="text" class="action-button" @click="showTestCardModal(device.id)">
                  {{ $t('optionsManagement.testCard') }}
                </base-button>
                <base-button variant="text" class="action-button" @click="restartDevice(device.id)">
                  {{ $t('optionsManagement.restartDevice') }}
                </base-button>
              </div>
            </options-management>
            <modal
              size="lg"
              :visible="testCardModal"
              @modal-close="testCardModal = false">
              <div class="test-card-header">
                <img src="/assets/images/delete-warning.svg" alt="" class="warning-img">
                <h2>{{ $t('tvPage.testCardModalTitle') }}</h2>
              </div>
              <div class="test-card-body">
                <p class="description">{{ $t('tvPage.testCardModalDescription') }}</p>
                <base-button variant="primary" @click="testCard(device.id)">
                  {{ $t('tvPage.testAgainBtn') }}
                </base-button>
              </div>
            </modal>
            <modal
              :visible="changePinModal"
              size="xs"
              @modal-close="changePinModal = false"
              :close-button-visible="false">
              <div class="change-pin-header">
                <h2>{{ $t('tvPage.changePinModalTitle') }}</h2>
              </div>
              <div class="test-card-body" v-autofocus>
                <validation-observer ref="pinObs"
                                     tag="form"
                                     v-slot="{ errors }"
                                     @submit.prevent="submitChangePinForm">
                  <div class="input-wrapper">
                    <div class="input-label">{{ $t('tvPage.newPinModalLabel') }}</div>
                    <validation-provider rules="numeric"
                                         :mode="inputNumberSubmitted ? 'eager' : 'passive'"
                                         v-for="(cell, index) in cells"
                                         :key="cell.key" v-slot="{ errors }">
                      <div class="pin-wrapper">
                        <input :ref="`pin-${index}`"
                               v-model.trim="cell.value"
                               class="pin-input"
                               @input="onCellChange(index)"
                               @focus="onFocus(index)"
                               @keydown.delete="onCellErase(index, $event)"
                               @keydown.left="focusPreviousCell"
                               @keydown.right="focusNextCell"
                               maxlength="1"
                               :class="{'validation' : errors.length}"
                        />
                      </div>
                    </validation-provider>
                    <span v-if="checkIfFormInvalid(errors)" class="error-msg">{{ $t('errors.wrongFormat') }}</span>
                  </div>
                  <p class="change-pin-description">{{ $t('tvPage.changePinModalDescription') }}</p>
                  <div class="button-container">
                    <base-button :disabled="!isFilled && !checkIfFormInvalid(errors)" :loading="dataLoading" variant="primary" type="submit">
                      {{ $t('tvPage.saveBtn') }}
                    </base-button>
                    <button class="cancel-change" @click="cancelPinChange" type="button">{{ $t('tvPage.cancelBtn') }}</button>
                  </div>
                </validation-observer>
              </div>
            </modal>
          </div>
        </template>
      </div>
      <div class="channels-list" v-else :key="'list'">
        <television-channel-list :channel-data="channelData" @close-channels="handleCloseChannels"/>
      </div>
    </transition>
  </div>
</template>

<script>
import OptionsManagement from '@/components/common/options-management/options-management'
import OptionsItem from '@/components/common/options-management/options-item'
import Modal from '@/components/common/modal/modal'
import { addDataLayerEvent } from '@/services/dataLayer'
import TelevisionChannelList from '@/components/services/resources/television-channel-list'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import { makeQueryString } from '@/utils/queryParser'
import fileDownloader from '@/mixins/fileDownloader'
import selectionBlockEvents from '@/mixins/selectionBlockEvents'

export default {
  name: 'television-resources',
  components: { TelevisionChannelList, OptionsItem, OptionsManagement, Modal },
  data () {
    return {
      focusIndex: 0,
      cells: [],
      inputNumberSubmitted: false,
      editNameMode: false,
      deviceHelper: {},
      testCardModal: false,
      changePinModal: false,
      buttonDisabled: false,
      dataLoading: false,
      currentId: '',
      valid: true,
      isChannelListShown: false,
      channelData: [],
      hasTVInstructions: false
    }
  },
  props: {
    numberOfDigits: {
      type: Number,
      required: false,
      default: 4
    },
    resourcesData: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  mixins: [errorMessageHandler, fileDownloader, selectionBlockEvents],
  computed: {
    isFilled () {
      return this.cells.every(cell => cell.value !== '')
    },
    pin () {
      return this.cells.reduce((pin, cell) => pin + cell.value, '')
    },
    channelsCount () {
      if (this.$props.resourcesData && this.$props.resourcesData.averageServiceUsages && this.$props.resourcesData.averageServiceUsages.length) {
        const count = {}
        this.$props.resourcesData.averageServiceUsages[0].resources.map(item => {
          if (item.id === 'svi') count.allChannels = item.value
          else if (item.id === 'hd') count.hdChannels = item.value
        })

        return count
      }
      return {}
    }
  },
  methods: {
    init () {
      for (let key = 0; key < this.numberOfDigits; key += 1) {
        this.cells.push({ key, value: '' })
      }
    },
    onCellChange (index) {
      const isThisCellFilled = this.cells[index].value.length
      if (!isThisCellFilled) {
        this.cells[index].value = ''
        return
      }
      this.focusNextCell()
    },
    onCellErase (index, e) {
      const isThisCellFilled = this.cells[index].value.length
      if (!isThisCellFilled) {
        this.focusPreviousCell()
        e.preventDefault()
      }
    },
    onFocus (index) {
      this.focusIndex = index
    },
    focusPreviousCell () {
      if (!this.focusIndex) return
      this.focusCellByIndex(this.focusIndex - 1)
    },
    focusNextCell () {
      if (this.focusIndex === this.numberOfDigits - 1) return
      this.focusCellByIndex(this.focusIndex + 1)
    },
    focusCellByIndex (index = 0) {
      const ref = `pin-${index}`
      const el = this.$refs[ref][0]

      el.focus()
      el.select()
      this.focusIndex = index
    },
    checkIfFormInvalid (errors) {
      if (Object.keys(errors).length) {
        return Object.values(errors).some(error => error.length)
      }
    },
    async submitChangePinForm () {
      this.dataLoading = true
      this.inputNumberSubmitted = true
      const valid = await this.$refs.pinObs[0].validate()
      if (valid && this.pin.length === 4) {
        this.changePin()
      } else {
        this.valid = false
        this.dataLoading = false
        this.buttonDisabled = false
      }
    },
    enterEditMode (deviceId, deviceName) {
      this.$set(this.deviceHelper, deviceId, {})
      this.$set(this.deviceHelper[deviceId], 'name', deviceName)
      this.$set(this.deviceHelper[deviceId], 'editMode', true)
    },
    exitEditMode () {
      for (const device in this.deviceHelper) {
        if (this.deviceHelper[device].editMode) {
          this.$set(this.deviceHelper[device], 'editMode', false)
        }
      }
    },
    async editText (id, newName, index) {
      const isValid = await this.$refs[`nameObs${index}`][0].validate()
      const subscriberIdentity = this.$props.resourcesData.subscriberIdentity
      if (isValid) {
        this.$apiRequest.put('user/device/changename', { deviceId: id, newDeviceName: newName, subscriberIdentity: subscriberIdentity })
          .then(response => {
            if (response.status === 200) {
              this.exitEditMode()
              this.sendRefreshDataEvent()
              addDataLayerEvent('uspesno_izmenjen_naziv_uredjanja')
            }
          })
          .catch(e => {
            this.handleErrorMessage(e.response.data)
          })
      }
    },
    restartDevice (id) {
      this.$apiRequest.post('user/device/restart', {
        deviceId: id,
        subscriberIdentity: this.$props.resourcesData.subscriberIdentity
      })
        .then(response => {
          if (response.status === 200) {
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t('toastMessages.successRestartMessage')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    showTestCardModal (id) {
      this.testCardModal = true
      this.testCard(id)
    },
    testCard (id) {
      this.$apiRequest.post('user/device/testtvcard', {
        deviceId: id,
        subscriberIdentity: this.$props.resourcesData.subscriberIdentity
      })
        .then(response => {
          if (response.status === 200) {
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t('toastMessages.successTestCardMessage')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    cancelPinChange () {
      this.changePinModal = false
      this.resetData()
    },
    showPinModal (id) {
      this.changePinModal = true
      this.currentId = id
    },
    changePin () {
      this.$apiRequest.put('user/device/changepin', {
        deviceId: this.currentId,
        subscriberIdentity: this.$props.resourcesData.subscriberIdentity,
        newPinCode: this.pin
      })
        .then(response => {
          if (response.status === 200) {
            this.onSuccessModalClose()
            this.toast('success', {
              title: this.$t('toastMessages.successToastTitle'),
              message: this.$t('toastMessages.successChangePinMessage')
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.onSuccessModalClose()
          this.dataLoading = false
        })
    },
    resetData () {
      this.cells = []
      this.init()
    },
    onSuccessModalClose () {
      this.changePinModal = false
      this.resetData()
      this.sendRefreshDataEvent()
    },
    sendRefreshDataEvent () {
      if (this.$props.resourcesData.subscriberIdentity) {
        this.$emit('refresh-data', this.$props.resourcesData.subscriberIdentity)
      }
    },
    downloadInstructions () {
      this.$origamiRequest.get('device/instructions/tv', { responseType: 'arraybuffer' })
        .then(response => {
          if (response.status === 200) {
            addDataLayerEvent('preuzmi_uputstvo')
            this.downloadFile(response, 'tv_uredjaj_uputstvo.pdf') // fileDownloader mixin
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
    },
    openChannelList () {
      this.isChannelListShown = true
      this.$emit('hide-contract-bar', true)
      this.$emit('hide-banner', true)
      this.$emit('subpage-open', true)
    },
    getChannels (forceFetch = false) {
      this.$loader.show()
      const { tvPackageCode, subscriberIdentity } = this.$props.resourcesData
      const qs = makeQueryString({ packageCode: tvPackageCode, subscriberIdentity: subscriberIdentity })
      if (!this.channelData.length || forceFetch) {
        this.$apiRequest.get(`user/service/tvchannels${qs}`)
          .then(response => {
            this.channelData = response.data.categories
            this.openChannelList()
            this.$loader.hide()
          })
          .catch(e => {
            this.handleErrorMessage(e.response.data)
            this.$loader.hide()
          })
      } else {
        this.openChannelList()
        this.$loader.hide()
      }
    },
    handleCloseChannels () {
      this.isChannelListShown = false
      this.$emit('subpage-open', false)
      this.$emit('hide-contract-bar', false)
      this.$emit('hide-banner', false)
    },
    checkIfHasInstructions () {
      this.hasTVInstructions = !this.$root.$el.classList.contains('hide-tv-instructions')
    }
  },
  created () {
    this.init()
  },
  mounted () {
    this.$nextTick(() => {
      this.unblockSidebarSelection(false)
      this.checkIfHasInstructions()
    })
  }
}
</script>

<style scoped lang="scss">
.tv-overview {
  padding-top: 2rem;

  .title {
    font-size: 2.4rem;
    font-weight: normal;
    text-transform: uppercase;
    padding-bottom: 0.8rem;
    margin-bottom: 2.4rem;
    border-bottom: 1px solid $form-border-color;
  }

  .channel-info {
    width: 100%;
    padding: 1.6rem;
    background-color: $white;
    border-radius: 5px;

    @media (map_get($breakpoints, sm)) {
      max-width: 325px;
    }

    .info-wrapper {
      display: flex;
      align-items: center;
      margin-bottom: 1.6rem;

      .info-side {
        flex: 1 1 50%;
        max-width: 50%;
        font-size: 1.6rem;
        font-weight: 600;
        display: flex;
        align-items: center;
        padding: 0 0.8rem;

        span {
          font-size: 2.4rem;
          font-weight: bold;
          color: $primary;
          margin-left: 1.6rem;
        }
      }

      .info-side + .info-side {
        border-left: 1px solid $grey;
        justify-content: flex-end;
      }
    }
  }

  .tv-actions {
    margin-top: 2.4rem;

    .manual-button {
      font-size: 1.2rem;
      color: $text;
      padding: 0 !important;

      i {
        color: $primary;
        margin-left: 0.8rem;
        font-size: 1.8rem;
        vertical-align: -4px;
      }
    }

    .option-description {
      font-size: 1rem;
      color: $dark-grey;
      margin: 0;
    }

    .option-title {
      font-size: 1.4rem;
      font-weight: 600;
      color: $text;
      margin: 0;
    }
  }

  .device-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;

    @media (map_get($breakpoints, exsm)) {
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: center;
    }

    @media (map_get($breakpoints, sm)) {
      align-items: flex-start;
    }

    .action-button {
      font-size: 1.4rem;
      text-decoration: underline;
      flex: 1 1 auto;
      margin-bottom: 0.8rem;
      font-weight: 600;
      padding: 0.5rem 0 !important;

      @media (map_get($breakpoints, exsm)) {
        flex: 1 1 33%;
        margin: 0;
      }

      &:not(:first-child) {
        border-left: none;

        @media (map_get($breakpoints, sm)) {
          border-left: 1px solid $grey;
        }
      }

      &:nth-child(2) {
        text-align: right;
        border-left: none;
        border-left: 1px solid $grey;

        @media (map_get($breakpoints, sm)) {
          text-align: center;
        }
      }

      &:first-child {
        text-align: left;
      }

      &:last-child {
        text-align: left;
        width: 100%;

        @media (map_get($breakpoints, sm)) {
          text-align: right;
          width: auto;
        }
      }

      i {
        font-size: 16px;
        vertical-align: -4px;
        margin-left: 0.8rem;
      }
    }
  }

  .test-card-header {
    text-align: center;

    img {
      max-width: 112px;
      width: 100%;
      height: auto;
      display: block;
      margin: 0.8rem auto;
    }

    h2 {
      font-size: 3.2rem;
      color: $text;
      margin-top: 2.4rem;
      margin-bottom: 1.6rem;
      font-weight: normal;
    }
  }

  .test-card-body {
    text-align: center;

    .description {
      margin: 4rem 0;
    }
  }

  .cancel-change {
    min-width: 120px;
    color: $primary;
    font-weight: 600;
    font-size: 1.6rem;
  }

  .input-wrapper {
    display: inline-flex;
    position: relative;

    .input-label {
      position: absolute;
      text-align: left;
      top: -20px;
      font-size: 1.2rem;
      color: $dark-grey;

      span {
        position: relative;
      }

    }
  }

  .change-pin-description {
    margin: 2rem 0 4rem;
  }

  .change-pin-header {
    text-align: center;

    h2 {
      font-size: 3.2rem;
      color: $text;
      margin-top: 2.4rem;
      font-weight: normal;
      margin-bottom: 5rem;
    }
  }
}

.pin-wrapper {
  display: inline-flex;
  margin-bottom: 2rem;
  position: relative;

  .pin-input {
    text-align: left;
    outline: none;
    display: block;
    background: $form-field-background;
    border: 1px solid transparent;
    border-radius: 5px;
    font-size: 1.5rem;
    color: $font-color;
    width: 40px;
    height: 40px;
    margin-right: 8px;
    padding: 1.4rem;

    &.validation {
      border: 1px solid $primary;
    }
  }

  input.invalid {
    border: 1px solid $error;
  }
}

.error-msg {
  display: block;
  position: absolute;
  top: 50px;
  left: 0;
  text-align: left;
  font-size: 1rem;
  line-height: 1;
  color: $error;

}

.pin-input::-webkit-outer-spin-button,
.pin-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.pin-input[type="number"] {
  -moz-appearance: textfield;
}

</style>
